"use client";

import {
    ScrollText,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";
import { brandConfig } from "@/lib/brandConfig";

const aszfEffectiveDate =
    process.env
        .NEXT_PUBLIC_ASZF_EFFECTIVE_DATE ||
    "2026-10-14";

const formatAszfEffectiveDateHu = (
    value
) => {
    const [
        year,
        month,
        day,
    ] = value.split("-");

    const months = [
        "január",
        "február",
        "március",
        "április",
        "május",
        "június",
        "július",
        "augusztus",
        "szeptember",
        "október",
        "november",
        "december",
    ];

    return `${year}. ${
        months[
            Number(month) - 1
        ]
    } ${Number(day)}.`;
};

const formatAszfEffectiveDateEn = (
    value
) => {
    const [
        year,
        month,
        day,
    ] = value.split("-");

    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    return `${Number(day)} ${
        months[
            Number(month) - 1
        ]
    } ${year}`;
};

const aszfEffectiveDateHu =
    formatAszfEffectiveDateHu(
        aszfEffectiveDate
    );

const aszfEffectiveDateEn =
    formatAszfEffectiveDateEn(
        aszfEffectiveDate
    );

const translations = {
    hu: {
        pageTitle: (
            <>
                általános szerződési
                <br />
                feltételek
            </>
        ),

        introduction:
            `a ${brandConfig.fullName.toLowerCase()} szolgáltatásaira vonatkozó feltételek.`,

        abbreviation:
            "ÁSZF",

        effectiveDate:
            `hatályos: ${aszfEffectiveDateHu} napjától`,

        translationNotice:
            null,

        sections: [
            {
                title:
                    "Szolgáltató",

                entries: [
                    `A ${brandConfig.fullName} üzemeltetője:`,

                    {
                        type:
                            "details",

                        items: [
                            brandConfig.legal.companyName,
                            `Székhely: ${brandConfig.legal.registeredOffice}`,
                            `Cégjegyzékszám: ${brandConfig.legal.companyRegistrationNumber}`,
                            `Adószám: ${brandConfig.legal.taxNumber}`,
                            `Képviselő: ${brandConfig.legal.representative}`,
                            `E-mail: ${brandConfig.legal.email}`,
                            `Telefon: ${brandConfig.legal.phone}`,
                            `Weboldal: ${brandConfig.legal.website}`,
                        ],
                    },

                    "A szolgáltatás helyszíne:",

                    {
                        type:
                            "details",

                        items: [
                            brandConfig.legal.serviceLocationName,
                            brandConfig.legal.serviceLocationAddress,
                        ],
                    },

                    `A továbbiakban: Szolgáltató vagy ${brandConfig.name}.`,
                ],
            },
            {
                title:
                    "Az ÁSZF hatálya",

                entries: [
                    `Jelen Általános Szerződési Feltételek a ${brandConfig.name} által nyújtott szabadidős és ügyességi játék szolgáltatás igénybevételére, valamint az ehhez kapcsolódó online és helyszíni jegyvásárlásra vonatkoznak.`,
                    `A jegy megvásárlásával, illetve a szolgáltatás igénybevételével a Vendég elfogadja jelen ÁSZF-et és a ${brandConfig.name} mindenkor hatályos Házirendjét.`,
                    "Online vásárlás esetén a vásárlás kizárólag az ÁSZF és a Házirend kifejezett elfogadását követően véglegesíthető.",
                    "A csapatépítő kapcsolatfelvételi űrlap használatára az alábbi külön rendelkezések vonatkoznak; a megkeresés beküldése önmagában nem minősül jegyvásárlásnak vagy a szolgáltatás megrendelésének.",
                ],
            },
            {
                title:
                    "A szolgáltatás",

                entries: [
                    `A ${brandConfig.name} ügyességi dobójátékot biztosító szabadidős szolgáltatás.`,
                    "Egy játékmenet időtartama 60 perc.",
                    `A ${brandConfig.name} területén 8 játékpálya található. A belépőjegy a lefoglalt 60 perces időtartamon belül a Vendég számára hozzáférhető pályák használatára jogosít, azok aktuális foglaltságának és a játékszervezésnek megfelelően.`,
                    "Egy turnus maximális létszáma 50 fő.",
                    "A jegy nem biztosít kizárólagos pályahasználatot, kivéve, ha a Vendég külön ilyen szolgáltatást vagy csomagot vásárol.",
                ],
            },
            {
                title:
                    "Csapatépítő megkeresések",

                entries: [
                    "A csapatépítő kapcsolatfelvételi űrlap díjmentes érdeklődésre és egyeztetés kezdeményezésére szolgál. A beküldés nem hoz létre foglalást vagy szolgáltatási szerződést, és nem keletkeztet fizetési kötelezettséget.",
                    "Az űrlapon 1 és 50 fő közötti tervezett létszám adható meg. A STRIKE egyszerre legfeljebb 50 vendéget tud fogadni; az adott időpontra rendelkezésre álló szabad kapacitást külön egyeztetjük.",
                    "Az opcionális üzenetben az érdeklődő további kérdést, elképzelést vagy megjegyzést adhat meg. Az automatikus válasz kizárólag a megkeresés beérkezését igazolja, nem jelent ajánlatelfogadást, időpont- vagy foglalás-visszaigazolást.",
                    "A program időpontját, végleges létszámát, árát és fizetési feltételeit külön egyeztetjük. Foglalás a külön egyeztetés során létrejövő kifejezett megállapodással vagy az online foglalási folyamat szabályai szerint jön létre. A megkeresés önmagában nem biztosít helyet vagy kizárólagos helyszínhasználatot.",
                    "Az űrlap adataira az Adatkezelési Tájékoztató csapatépítő kapcsolatfelvételi űrlapról szóló fejezete vonatkozik. A beküldés nem jelent hírlevél-feliratkozást.",
                ],
            },
            {
                title:
                    "Jegyárak és vásárlás",

                entries: [
                    "Az általános belépőjegy ára:",

                    {
                        type:
                            "highlight",

                        items: [
                            "Online vásárlás esetén: 4 990 Ft/fő + 250 Ft adminisztrációs díj.",
                            "Helyszíni vásárlás esetén: 5 500 Ft/fő.",
                        ],
                    },

                    "Az online vásárlás alapértelmezett fizetési módja a bankkártyás fizetés. A helyszíni vásárlás kizárólag bankkártyával vagy készpénzzel történhet, banki átutalásra a helyszínen nincs lehetőség.",
                    `Banki átutalásos fizetés kizárólag külön engedély alapján, elsősorban olyan intézmények, szervezetek vagy gazdasági szereplők részére érhető el, amelyek számára a bankkártyás fizetés nem megoldható. Az átutalásos fizetés igényét előzetesen e-mailben, a ${brandConfig.legal.email} címen kell jelezni.`,
                    `A banki átutalásos fizetési módot a foglalási rendszer kizárólag a ${brandConfig.name} előzetes engedélyezését követően teszi elérhetővé. A rendszer az átutalásos fizetést csak olyan foglalás esetén engedélyezi, amelynek kezdési időpontja a fizetési mód engedélyezésekor több mint 24 órára van. A lefoglalt kezdési időpontot megelőző 24 órán belül a rendszer banki átutalásos fizetést nem engedélyez.`,
                    "Az online bankkártyás fizetés a Stripe rendszerén keresztül történik.",

                    "Banki átutalás esetén a fizetendő összeget az alábbi bankszámlára kell átutalni:",

                    {
                        type:
                            "details",

                        items: [
                            `Kedvezményezett: ${brandConfig.legal.bankAccount.accountHolder}`,
                            `Bank: ${brandConfig.legal.bankAccount.bank}`,
                            `Bankszámlaszám: ${brandConfig.legal.bankAccount.accountNumber}`,
                            `IBAN: ${brandConfig.legal.bankAccount.iban}`,
                            `SWIFT / BIC: ${brandConfig.legal.bankAccount.swift}`,
                        ],
                    },

                    `Banki átutalás esetén a Vendég köteles az átutalás közleményében a ${brandConfig.name} által megadott rendelési vagy foglalási azonosítót feltüntetni. A fizetés teljesítettnek akkor minősül, amikor az összeg a Szolgáltató bankszámláján jóváírásra került.`,
                    "A számlázás elektronikus úton, automatikusan, a Billingo rendszerén keresztül történik.",
                    "Sikeres online vásárlást követően a Vendég a megadott e-mail-címre kapja meg a belépéshez szükséges elektronikus jegyet, illetve QR-kódot.",
                    "A Vendég felelőssége, hogy a vásárlás során helyes e-mail-címet és számlázási adatokat adjon meg.",
                    `A ${brandConfig.name} jogosult szolgáltatásainak, jegyeinek, csomagjainak és egyéb termékeinek árait bármikor módosítani. Az árváltozás kizárólag a módosítást követően kezdeményezett vásárlásokra és foglalásokra alkalmazandó, és nem érinti a korábban már véglegesített vásárlások és foglalások árát.`,
                    "A vásárlásra minden esetben a megrendelés véglegesítésekor a weboldalon vagy a helyszínen feltüntetett aktuális árak vonatkoznak.",
                ],
            },
            {
                title:
                    "Foglalás és érkezés",

                entries: [
                    "A jegy konkrét napra és konkrét kezdési időpontra érvényes.",
                    "A Vendég köteles a lefoglalt kezdési időpont előtt legalább 15 perccel megérkezni.",
                    "A játékmenet pontosan a lefoglalt kezdési időpontban indul. A játék megkezdését követően érkező Vendég a már megkezdett játékmenethez nem tud csatlakozni.",
                    `Késés esetén a ${brandConfig.name} nem köteles másik időpontot, pótidőt vagy jegyár-visszatérítést biztosítani.`,
                ],
            },
            {
                title:
                    "Időpont módosítása, jegy átruházása",

                entries: [
                    "A foglalás időpontja a lefoglalt kezdési időpont előtt legalább 24 órával díjmentesen módosítható, a szabad helyek függvényében.",
                    "A kezdési időpontot megelőző 24 órán belül a foglalás nem módosítható.",
                    "A megvásárolt jegy más személy részére átadható.",
                    "A jegy készpénzre nem váltható, és a Vendég általi lemondás esetén a jegy ára nem téríthető vissza.",
                    `A meghatározott napra és időpontra szóló ${brandConfig.name} szolgáltatás szabadidős tevékenységnek minősül, ezért az ilyen, meghatározott teljesítési időpontra szóló szolgáltatásra a jogszabály szerinti 14 napos indokolás nélküli elállási jog nem alkalmazható.`,
                ],
            },
            {
                title:
                    "Ajándékkártya",

                entries: [
                    `A ${brandConfig.name} ajándékkártyát kínál, amely a rajta feltüntetett vagy a hozzá tartozó egyedi kóddal nyilvántartott összeg erejéig használható fel a ${brandConfig.name} szolgáltatásainak megvásárlására.`,
                    "Az ajándékkártya a vásárlás napjától számított 1 évig érvényes. A lejárati idő elteltét követően az ajándékkártya nem használható fel, érvényessége nem hosszabbítható meg, és a fel nem használt összeg nem téríthető vissza.",
                    "Az ajándékkártya készpénzre nem váltható, annak ellenértéke készpénzben vagy más pénzügyi eszközzel nem igényelhető vissza.",
                    "Az ajándékkártya felhasználása a hozzá tartozó egyedi kóddal történik. A kód bizalmas adatnak minősül, annak biztonságos megőrzése a vásárló, illetve az ajándékozott felelőssége.",
                    "Az ajándékkártya kódját tilos nyilvánosan közzétenni, így különösen közösségi médiafelületen, nyilvános weboldalon, fórumon vagy más, harmadik személyek számára hozzáférhető felületen megosztani.",
                    `Az ajándékkártya felhasználásakor a rendszer a kód birtokosát tekinti felhasználásra jogosultnak. A ${brandConfig.name} nem vizsgálja és technikailag nem tudja ellenőrizni, hogy a kódot beváltó személy az eredeti vásárló, az ajándékozott vagy más személy.`,
                    "Az ajándékkártya összegét vagy rendelkezésre álló egyenlegét az a személy használhatja fel, aki az érvényes kódot elsőként szabályszerűen beváltja. A már felhasznált összeg újbóli felhasználására nincs lehetőség.",
                    `A ${brandConfig.name} nem vállal felelősséget az ajándékkártya kódjának elvesztéséből, jogosulatlan megismeréséből, továbbításából, nyilvánosságra hozatalából vagy harmadik személy általi felhasználásából eredő károkért vagy veszteségért, kivéve, ha a kód jogosulatlan hozzáférhetővé válása a ${brandConfig.name}-nak felróható okból következett be.`,
                    "Az ajándékkártya átruházható, azt nem kizárólag az eredeti vásárló használhatja fel.",
                    "Az ajándékkártya másik ajándékkártya megvásárlására nem használható fel.",
                    `Az ajándékkártya felhasználására az annak beváltásakor elérhető szolgáltatások és aktuális árak vonatkoznak. Az ajándékkártya pénzben kifejezett értéke az érvényességi idő alatt nem változik akkor sem, ha a ${brandConfig.name} szolgáltatásainak árai időközben módosulnak.`,
                    "Amennyiben az ajándékkártyával fizetendő vásárlás értéke meghaladja az ajándékkártyán rendelkezésre álló összeget, a különbözetet a vásárlás során elérhető más fizetési móddal kell kiegyenlíteni.",
                    "Az ajándékkártya felhasználására a jelen ÁSZF egyéb rendelkezései is megfelelően alkalmazandók.",
                ],
            },
            {
                title:
                    "Kedvezmények és kuponkódok",

                entries: [
                    `A ${brandConfig.name} időszakosan kedvezményeket, promóciókat, kuponokat és egyéb kedvezményre jogosító kódokat biztosíthat.`,
                    "Az egyes kedvezmények és kuponkódok felhasználhatóságára külön feltételek vonatkozhatnak, így különösen meghatározható azok érvényességi ideje, felhasználási alkalmainak száma, minimális vásárlási értéke, az érintett szolgáltatások köre és más kedvezményekkel való összevonhatósága.",
                    "A kupon vagy kedvezmény kizárólag annak érvényességi idején belül és a hozzá tartozó feltételek teljesülése esetén használható fel.",
                    "Eltérő rendelkezés hiányában több kupon vagy kedvezmény ugyanazon vásárlás során nem vonható össze.",
                    "A kuponok és kedvezmények készpénzre nem válthatók, azok pénzbeli ellenértéke nem igényelhető.",
                    `A ${brandConfig.name} jogosult kedvezményes ajánlatokat és promóciókat indítani, módosítani vagy megszüntetni. A módosítás vagy megszüntetés nem érinti azokat a kedvezményeket, amelyek tekintetében a Vendég a módosítást megelőzően már véglegesített vásárlással jogosultságot szerzett.`,
                ],
            },
            {
                title:
                    "Korhatár és részvételi feltételek",

                entries: [
                    "A játékban 10. életévét betöltött személy vehet részt.",
                    "14 éves kor alatt kizárólag szülő vagy más nagykorú kísérő felügyelete mellett lehet részt venni a játékban.",
                    "A játékban csak olyan személy vehet részt, aki a játékeszközök, a dobási terület és a pálya biztonságos használatára képes.",
                    `Amennyiben a Vendégnek a biztonságos játékhoz segítségre van szüksége, javasolt a részvétel feltételeiről előzetesen egyeztetni a ${brandConfig.name} munkatársaival.`,
                    `A ${brandConfig.name} teljes területén alkoholos ital fogyasztása tilos. A helyszínen kizárólag alkoholmentes italok fogyaszthatók.`,
                    "Alkohol, kábítószer vagy más, a biztonságos játékot befolyásoló szer hatása alatt álló személy a játékban nem vehet részt.",
                    `A ${brandConfig.name} munkatársai jogosultak az ilyen személy játékban való részvételét megtagadni. Ebben az esetben a jegy ára nem jár vissza.`,
                ],
            },
            {
                title:
                    "Biztonsági szabályok",

                entries: [
                    `A ${brandConfig.name} területén a Házirend és a játékvezetők, illetve a ${brandConfig.name} munkatársainak biztonsági utasításai kötelezőek.`,
                    "A játékeszközök kizárólag rendeltetésszerűen és a kijelölt területen használhatók.",
                    `Tilos a játékeszközöket más személy felé dobni vagy olyan módon használni, amely más személy testi épségét vagy a ${brandConfig.name} berendezéseit veszélyezteti.`,
                    `Súlyos vagy ismételt szabályszegés, veszélyeztető, agresszív vagy más Vendégeket súlyosan zavaró magatartás esetén a ${brandConfig.name} jogosult a Vendéget a játékból kizárni, illetve a helyszín elhagyására felszólítani.`,
                    "Ilyen esetben a jegy ára nem jár vissza.",
                ],
            },
            {
                title:
                    "Felelősség",

                entries: [
                    "A Vendég a játékban saját felelősségére vesz részt, köteles saját képességeit és fizikai állapotát figyelembe venni, valamint a biztonsági szabályokat betartani.",
                    `A ${brandConfig.name} nem felel azokért a sérülésekért vagy károkért, amelyek a Vendég szabályszegéséből, gondatlan magatartásából, a játékeszközök rendeltetésellenes használatából vagy a ${brandConfig.name} munkatársai utasításainak figyelmen kívül hagyásából erednek.`,
                    "Jelen rendelkezés nem érinti és nem korlátozza a Szolgáltató jogszabály alapján ki nem zárható felelősségét.",
                    `A Vendég köteles megtéríteni az általa szándékosan vagy gondatlanul a ${brandConfig.name} eszközeiben, berendezéseiben, helyiségeiben vagy más személy tulajdonában okozott kárt.`,
                ],
            },
            {
                title:
                    "Ruhatár és személyes tárgyak",

                entries: [
                    `A ${brandConfig.name} ruhatárat biztosít a Vendégek részére.`,
                    `A ${brandConfig.name} területére behozott pénzért, ékszerért, telefonért, elektronikai eszközért és egyéb értéktárgyakért a Szolgáltató csak a jogszabályban meghatározott esetekben és mértékben felel.`,
                    "A Vendég köteles személyes tárgyaira megfelelően vigyázni.",
                ],
            },
            {
                title:
                    "A szolgáltatás elmaradása és műszaki hiba",

                entries: [
                    `Amennyiben a ${brandConfig.name} a lefoglalt játékmenetet saját érdekkörében felmerülő okból nem tudja megtartani, a Vendég választása szerint:`,

                    {
                        type:
                            "list",

                        items: [
                            "a jegy teljes vételárának visszatérítésére, vagy",
                            "egy másik, rendelkezésre álló időpontra történő díjmentes átfoglalásra jogosult.",
                        ],
                    },

                    "Ugyanez alkalmazandó olyan műszaki meghibásodás vagy elháríthatatlan külső körülmény esetén, amely miatt a lefoglalt szolgáltatás teljes egészében nem vehető igénybe.",
                    "Részleges vagy rövid ideig tartó műszaki meghibásodás esetén, amennyiben a játék más pályákon érdemben folytatható, automatikusan nem jár teljes jegyár-visszatérítés.",
                    `Jelentős részleges szolgáltatáskimaradás esetén a ${brandConfig.name} az eset körülményeitől függően arányos kompenzációt, másik időpontot vagy indokolt esetben jegyár-visszatérítést biztosíthat, a fogyasztót jogszabály alapján megillető jogok sérelme nélkül.`,
                ],
            },
            {
                title:
                    "Panaszkezelés",

                entries: [
                    `A Vendég panaszát személyesen a ${brandConfig.name} helyszínén a teremfőnöknél, vagy elektronikus úton az alábbi címen teheti meg:`,

                    {
                        type:
                            "email",

                        value:
                            brandConfig.legal.email,
                    },

                    `A ${brandConfig.name} a panaszokat a hatályos fogyasztóvédelmi jogszabályok szerint kezeli és válaszolja meg.`,
                    "Fogyasztói jogvita esetén a fogyasztó jogosult békéltető testülethez fordulni.",
                    "A Szolgáltató székhelye szerint illetékes:",

                    {
                        type:
                            "details",

                        items: [
                            "Budapesti Békéltető Testület",
                            "1016 Budapest, Krisztina krt. 99.",
                            "Levelezési cím: 1253 Budapest, Pf. 10.",
                            "E-mail: bekelteto.testulet@bkik.hu",
                            "Telefon: +36 30 120 2326",
                        ],
                    },

                    "A békéltető testületi eljárás célja a fogyasztó és a vállalkozás közötti jogvita bírósági eljáráson kívüli rendezése.",
                ],
            },
            {
                title:
                    "Házirend",

                entries: [
                    `A ${brandConfig.name} Házirendje jelen ÁSZF részét képezi.`,
                    `A Házirend tartalmazza különösen a játék részletes biztonsági szabályait, a játékeszközök használatának módját, valamint a ${brandConfig.name} területén elvárt magatartási szabályokat.`,
                    "A Vendég a jegy megvásárlásával és a szolgáltatás igénybevételével vállalja a Házirend betartását.",
                ],
            },
            {
                title:
                    "Adatkezelés",

                entries: [
                    `A személyes adatok kezelésére a ${brandConfig.name} külön Adatkezelési Tájékoztatója vonatkozik, amely a weboldalon érhető el.`,
                    "Az online jegyvásárlás során szükséges személyes adatok kezelése a foglalás, a fizetés, a számlázás, a jegy kiállítása és a kapcsolattartás céljából történik.",
                    "A csapatépítő űrlapon megadott adatokat a megkeresés megválaszolásához és a program egyeztetéséhez kezeljük; a részletes jogalapokat, megőrzési időket és érintetti jogokat az Adatkezelési Tájékoztató tartalmazza.",
                ],
            },
            {
                title:
                    "Záró rendelkezések",

                entries: [
                    "A jelen ÁSZF-ben nem szabályozott kérdésekben a mindenkor hatályos magyar jogszabályok, különösen a Polgári Törvénykönyv, a fogyasztóvédelmi jogszabályok, valamint a fogyasztó és vállalkozás közötti szerződésekre vonatkozó rendelkezések irányadók.",
                    `A ${brandConfig.name} jogosult jelen ÁSZF-et a jogszabályok keretei között módosítani. A módosítás a már megkötött szerződéseket és korábban megvásárolt jegyeket a Vendég hátrányára nem érinti.`,

                    {
                        type:
                            "highlight",

                        items: [
                            `Hatályos: ${aszfEffectiveDateHu}`,
                        ],
                    },
                ],
            },
        ],
    },

    en: {
        pageTitle: (
            <>
                general terms and
                <br />
                conditions
            </>
        ),

        introduction:
            `terms applicable to the services of ${brandConfig.fullName.toLowerCase()}.`,

        abbreviation:
            "Terms and Conditions",

        effectiveDate:
            `effective from ${aszfEffectiveDateEn}`,

        translationNotice:
            "This English translation is provided for information purposes only. In the event of any discrepancy, the Hungarian version shall prevail.",

        sections: [
            {
                title:
                    "Service provider",

                entries: [
                    `The operator of ${brandConfig.fullName} is:`,

                    {
                        type:
                            "details",

                        items: [
                            brandConfig.legal.companyName,
                            `Registered office: ${brandConfig.legal.registeredOffice}`,
                            `Company registration number: ${brandConfig.legal.companyRegistrationNumber}`,
                            `Tax number: ${brandConfig.legal.taxNumber}`,
                            `Represented by: ${brandConfig.legal.representative}`,
                            `E-mail: ${brandConfig.legal.email}`,
                            `Phone: ${brandConfig.legal.phone}`,
                            `Website: ${brandConfig.legal.website}`,
                        ],
                    },

                    "Location of the service:",

                    {
                        type:
                            "details",

                        items: [
                            brandConfig.legal.serviceLocationName,
                            brandConfig.legal.serviceLocationAddress,
                        ],
                    },

                    `Hereinafter referred to as the Service Provider or ${brandConfig.name}.`,
                ],
            },
            {
                title:
                    "Scope of the Terms and Conditions",

                entries: [
                    `These General Terms and Conditions apply to the use of the leisure and skill-based gaming services provided by ${brandConfig.name}, as well as to the related online and on-site purchase of tickets.`,
                    `By purchasing a ticket or using the service, the Guest accepts these Terms and Conditions and the current House Rules of ${brandConfig.name}.`,
                    "For online purchases, the transaction may only be completed after the Guest has expressly accepted the Terms and Conditions and the House Rules.",
                    "Use of the team-building enquiry form is covered by the separate provisions below; submitting an enquiry does not itself constitute a ticket purchase or an order for the service.",
                ],
            },
            {
                title:
                    "The service",

                entries: [
                    `${brandConfig.name} provides a leisure service based on skill-based throwing games.`,
                    "Each game session lasts 60 minutes.",
                    `${brandConfig.name} has 8 game courses. During the reserved 60-minute period, the admission ticket entitles the Guest to use the courses available to them, subject to current occupancy and the organisation of the game.`,
                    "The maximum capacity of one session is 50 guests.",
                    "The ticket does not provide exclusive use of a course unless the Guest purchases a separate service or package that expressly includes exclusive use.",
                ],
            },
            {
                title:
                    "Team-building enquiries",

                entries: [
                    "The team-building enquiry form is a free way to request information and start discussing an event. Submission does not create a booking or a service contract and does not give rise to any payment obligation.",
                    "The form accepts an estimated group size of 1 to 50 guests. STRIKE can accommodate a maximum of 50 guests at the same time; available capacity for a particular time is confirmed separately.",
                    "The optional message can include further questions, ideas or comments. The automatic reply only acknowledges receipt of the enquiry; it does not constitute acceptance of an offer or confirmation of a time slot or booking.",
                    "The event date, final group size, price and payment terms are agreed separately. A booking is created through an express agreement reached during those discussions or under the online booking process. An enquiry alone does not reserve capacity or provide exclusive use of the venue.",
                    "Data submitted through the form is covered by the team-building enquiry form section of the Privacy Notice. Submission does not subscribe the enquirer to a newsletter.",
                ],
            },
            {
                title:
                    "Ticket prices and purchases",

                entries: [
                    "The price of a standard admission ticket is:",

                    {
                        type:
                            "highlight",

                        items: [
                            "Online purchase: 4,990 HUF per person + 250 HUF administration fee.",
                            "Purchase at the venue: 5,500 HUF per person.",
                        ],
                    },

                    "The default payment method for online purchases is payment by bank card. Purchases at the venue may only be paid by bank card or cash; bank transfer is not available for on-site purchases.",
                    `Payment by bank transfer is only available subject to prior approval, primarily for institutions, organisations or businesses that are unable to make payment by bank card. Requests for payment by bank transfer must be submitted in advance by e-mail to ${brandConfig.legal.email}.`,
                    `The booking system makes bank transfer available only after prior approval by ${brandConfig.name}. The system only permits bank transfer for bookings where the reserved starting time is more than 24 hours away at the time the payment method is enabled. Within 24 hours of the reserved starting time, the system does not permit payment by bank transfer.`,
                    "Online bank card payments are processed through Stripe.",

                    "For payments by bank transfer, the amount due must be transferred to the following bank account:",

                    {
                        type:
                            "details",

                        items: [
                            `Account holder: ${brandConfig.legal.bankAccount.accountHolder}`,
                            `Bank: ${brandConfig.legal.bankAccount.bank}`,
                            `Account number: ${brandConfig.legal.bankAccount.accountNumber}`,
                            `IBAN: ${brandConfig.legal.bankAccount.iban}`,
                            `SWIFT / BIC: ${brandConfig.legal.bankAccount.swift}`,
                        ],
                    },

                    `For bank transfers, the Guest must include the order or booking reference provided by ${brandConfig.name} in the payment reference. Payment is considered completed when the amount has been credited to the Service Provider’s bank account.`,
                    "Invoices are issued electronically and automatically through Billingo.",
                    "Following a successful online purchase, the Guest receives the electronic ticket and QR code required for entry at the e-mail address provided.",
                    "The Guest is responsible for providing a correct e-mail address and accurate billing information during the purchase.",
                    `${brandConfig.name} may change the prices of its services, tickets, packages and other products at any time. Price changes apply only to purchases and bookings initiated after the change and do not affect the price of purchases or bookings already completed.`,
                    "The prices applicable to a purchase are the current prices displayed on the website or at the venue at the time the order is completed.",
                ],
            },
            {
                title:
                    "Booking and arrival",

                entries: [
                    "The ticket is valid for the specific date and starting time selected during booking.",
                    "The Guest must arrive at least 15 minutes before the reserved starting time.",
                    "The game starts exactly at the reserved starting time. Guests arriving after the game has started cannot join the session already in progress.",
                    `In the event of late arrival, ${brandConfig.name} is not required to provide another session, additional playing time or a refund of the ticket price.`,
                ],
            },
            {
                title:
                    "Changing a booking and transferring a ticket",

                entries: [
                    "The booking may be changed free of charge at least 24 hours before the reserved starting time, subject to availability.",
                    "The booking cannot be changed within 24 hours of the reserved starting time.",
                    "A purchased ticket may be transferred to another person.",
                    "The ticket cannot be exchanged for cash, and its price is not refundable if the booking is cancelled by the Guest.",
                    `The ${brandConfig.name} service provided for a specific date and time qualifies as a leisure activity. Therefore, the statutory 14-day right of withdrawal without giving reasons does not apply to services scheduled for a specific date or time.`,
                ],
            },
            {
                title:
                    "Gift cards",

                entries: [
                    `${brandConfig.name} offers gift cards which may be used to purchase ${brandConfig.name} services up to the monetary value shown on the gift card or recorded in connection with its unique code.`,
                    "Gift cards are valid for 1 year from the date of purchase. After the expiry date, the gift card can no longer be used, its validity cannot be extended and any unused value is non-refundable.",
                    "Gift cards cannot be redeemed or exchanged for cash, and their value cannot be refunded in cash or by any other financial means.",
                    "Gift cards are redeemed using their unique code. The code must be treated as confidential information, and the purchaser or recipient is responsible for keeping it secure.",
                    "The gift card code must not be published or shared publicly, including on social media, public websites, forums or any other platform accessible to third parties.",
                    `When a gift card is redeemed, the system treats the holder of the valid code as the person entitled to use it. ${brandConfig.name} does not verify and cannot technically determine whether the person redeeming the code is the original purchaser, the intended recipient or another person.`,
                    "The value or available balance of the gift card may be used by the person who first validly redeems the code. Any amount already used cannot be redeemed again.",
                    `${brandConfig.name} is not liable for any loss or damage resulting from the loss, unauthorised disclosure, sharing, publication or use of a gift card code by a third party, unless the unauthorised disclosure occurred due to a cause attributable to ${brandConfig.name}.`,
                    "Gift cards are transferable and may be used by a person other than the original purchaser.",
                    "Gift cards cannot be used to purchase another gift card.",
                    `Services purchased using a gift card are subject to the services and prices available at the time of redemption. The monetary value of the gift card itself does not change during its validity period even if ${brandConfig.name} changes the prices of its services.`,
                    "If the value of a purchase exceeds the available value of the gift card, the remaining amount must be paid using another payment method available during the purchase.",
                    "The other provisions of these Terms and Conditions also apply to the use of gift cards as appropriate.",
                ],
            },
            {
                title:
                    "Discounts and promotional codes",

                entries: [
                    `${brandConfig.name} may from time to time offer discounts, promotions, coupons and other promotional codes.`,
                    "Individual discounts and promotional codes may be subject to specific conditions, including their validity period, number of permitted uses, minimum purchase value, eligible services and whether they may be combined with other discounts.",
                    "A coupon or discount may only be used within its validity period and where all applicable conditions are met.",
                    "Unless otherwise stated, multiple coupons or discounts cannot be combined in the same purchase.",
                    "Coupons and discounts cannot be redeemed for cash and no cash equivalent may be claimed.",
                    `${brandConfig.name} may introduce, modify or discontinue promotional offers and discounts. Any modification or discontinuation does not affect a discount to which a Guest has already become entitled through a purchase completed before the change.`,
                ],
            },
            {
                title:
                    "Age limit and participation requirements",

                entries: [
                    "Only persons who have reached the age of 10 may participate in the game.",
                    "Guests under the age of 14 may only participate under the supervision of a parent or another adult accompanying person.",
                    "Only persons capable of using the game equipment, throwing area and courses safely may participate.",
                    `If a Guest requires assistance to play safely, the Guest is advised to discuss the participation requirements with ${brandConfig.name} staff in advance.`,
                    `The consumption of alcoholic beverages is prohibited throughout the ${brandConfig.name} premises. Only non-alcoholic drinks may be consumed at the venue.`,
                    "Persons under the influence of alcohol, drugs or any other substance that may affect safe participation are not permitted to play.",
                    `${brandConfig.name} staff may refuse participation to such persons. In this case, the ticket price will not be refunded.`,
                ],
            },
            {
                title:
                    "Safety rules",

                entries: [
                    `Guests must comply with the House Rules and all safety instructions issued by game hosts and ${brandConfig.name} staff.`,
                    "Game equipment may only be used for its intended purpose and within the designated area.",
                    `It is prohibited to throw game equipment towards another person or use it in any way that endangers another person or ${brandConfig.name} equipment.`,
                    `In the event of serious or repeated rule violations, dangerous or aggressive conduct, or conduct that seriously disturbs other Guests, ${brandConfig.name} may exclude the Guest from the game and require them to leave the venue.`,
                    "In such cases, the ticket price will not be refunded.",
                ],
            },
            {
                title:
                    "Liability",

                entries: [
                    "Guests participate in the game at their own responsibility and must take their abilities and physical condition into account and comply with all safety rules.",
                    `${brandConfig.name} is not liable for injuries or damage resulting from a Guest’s breach of the rules, negligent conduct, improper use of the equipment or failure to follow the instructions of ${brandConfig.name} staff.`,
                    "This provision does not affect or limit any liability of the Service Provider that cannot be excluded under applicable law.",
                    `The Guest must compensate for any damage intentionally or negligently caused to ${brandConfig.name} equipment, facilities or premises, or to the property of another person.`,
                ],
            },
            {
                title:
                    "Cloakroom and personal belongings",

                entries: [
                    `${brandConfig.name} provides a cloakroom for Guests.`,
                    `The Service Provider is only liable for money, jewellery, phones, electronic devices and other valuables brought into the ${brandConfig.name} premises in the cases and to the extent required by applicable law.`,
                    "Guests are responsible for taking appropriate care of their personal belongings.",
                ],
            },
            {
                title:
                    "Cancellation of the service and technical faults",

                entries: [
                    `If ${brandConfig.name} is unable to provide the reserved session for a reason within its control, the Guest may choose between:`,

                    {
                        type:
                            "list",

                        items: [
                            "a refund of the full ticket price, or",
                            "a free transfer to another available session.",
                        ],
                    },

                    "The same applies where a technical fault or unavoidable external circumstance prevents the reserved service from being used in full.",
                    "Where a partial or short technical fault occurs and the game can meaningfully continue on other courses, a full refund is not automatically payable.",
                    `In the event of a significant partial interruption, ${brandConfig.name} may provide proportionate compensation, another session or, where justified, a refund, depending on the circumstances and without prejudice to the Guest’s statutory consumer rights.`,
                ],
            },
            {
                title:
                    "Complaints",

                entries: [
                    `Guests may submit complaints in person to the venue manager at ${brandConfig.name} or electronically at:`,

                    {
                        type:
                            "email",

                        value:
                            brandConfig.legal.email,
                    },

                    `${brandConfig.name} handles and responds to complaints in accordance with applicable consumer protection legislation.`,
                    "In the event of a consumer dispute, the consumer may apply to a conciliation body.",
                    "The competent body based on the registered office of the Service Provider is:",

                    {
                        type:
                            "details",

                        items: [
                            "Budapesti Békéltető Testület",
                            "1016 Budapest, Krisztina krt. 99.",
                            "Postal address: 1253 Budapest, Pf. 10.",
                            "E-mail: bekelteto.testulet@bkik.hu",
                            "Phone: +36 30 120 2326",
                        ],
                    },

                    "The purpose of the conciliation procedure is to settle consumer disputes between consumers and businesses outside court proceedings.",
                ],
            },
            {
                title:
                    "House Rules",

                entries: [
                    `The ${brandConfig.name} House Rules form part of these Terms and Conditions.`,
                    `The House Rules contain detailed safety rules, instructions for using the game equipment and the standards of conduct expected on the ${brandConfig.name} premises.`,
                    "By purchasing a ticket and using the service, the Guest agrees to comply with the House Rules.",
                ],
            },
            {
                title:
                    "Data processing",

                entries: [
                    `The processing of personal data is governed by ${brandConfig.name}'s separate Privacy Notice, which is available on the website.`,
                    "Personal data required during online ticket purchases is processed for booking, payment, invoicing, ticket issuance and communication purposes.",
                    "Details submitted through the team-building form are processed to respond to the enquiry and discuss the event; the Privacy Notice sets out the legal bases, retention periods and data subject rights.",
                ],
            },
            {
                title:
                    "Final provisions",

                entries: [
                    "Matters not regulated by these Terms and Conditions are governed by the applicable laws of Hungary, in particular the Hungarian Civil Code, consumer protection legislation and the provisions governing contracts between consumers and businesses.",
                    `${brandConfig.name} may amend these Terms and Conditions within the limits permitted by law. Amendments will not adversely affect contracts already concluded or tickets previously purchased.`,

                    {
                        type:
                            "highlight",

                        items: [
                            `Effective from ${aszfEffectiveDateEn}.`,
                        ],
                    },
                ],
            },
        ],
    },
};

function LegalSection({
    number,
    title,
    entries,
}) {
    return (
        <section className="border-t-[3px] border-black py-8 first:border-t-0 first:pt-0 md:py-12">
            <div className="flex items-start gap-4 md:gap-7">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-black text-[13px] font-black text-white md:h-14 md:w-14 md:text-[16px]">
                    {String(number).padStart(
                        2,
                        "0"
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <h2 className="text-[20px] font-black leading-[1.1] md:text-[30px]">
                        {title}
                    </h2>

                    <div className="mt-6 space-y-5 text-[15px] leading-[1.55] text-neutral-700 md:mt-8 md:text-[18px] md:leading-[1.7]">
                        {entries.map(
                            (
                                entry,
                                index
                            ) => (
                                <LegalEntry
                                    key={
                                        `${number}-${index}`
                                    }
                                    entry={
                                        entry
                                    }
                                />
                            )
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function LegalEntry({
    entry,
}) {
    if (
        typeof entry ===
        "string"
    ) {
        return (
            <p>
                {entry}
            </p>
        );
    }

    if (
        entry.type ===
        "list"
    ) {
        return (
            <ul className="list-disc space-y-2 pl-5 marker:text-black md:pl-7">
                {entry.items.map(
                    (item) => (
                        <li
                            key={
                                item
                            }
                        >
                            {item}
                        </li>
                    )
                )}
            </ul>
        );
    }

    if (
        entry.type ===
        "highlight"
    ) {
        return (
            <div className="border-l-[3px] border-black pl-5">
                {entry.items.map(
                    (
                        item,
                        index
                    ) => (
                        <p
                            key={
                                item
                            }
                            className={
                                index > 0
                                    ? "mt-3"
                                    : ""
                            }
                        >
                            {item}
                        </p>
                    )
                )}
            </div>
        );
    }

    if (
        entry.type ===
        "details"
    ) {
        return (
            <div>
                {entry.items.map(
                    (
                        item,
                        index
                    ) => (
                        <p
                            key={
                                item
                            }
                            className={
                                index === 0
                                    ? "font-black text-black"
                                    : ""
                            }
                        >
                            {item}
                        </p>
                    )
                )}
            </div>
        );
    }

    if (
        entry.type ===
        "email"
    ) {
        return (
            <a
                href={`mailto:${entry.value}`}
                className="inline-block cursor-pointer font-black text-black underline underline-offset-4"
            >
                {entry.value}
            </a>
        );
    }

    return null;
}

export default function AszfPage() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            <section className="bg-[#dedede] text-black">

                {/* OLDAL TETEJE */}
                <div className="mx-auto flex min-h-[330px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-12 text-center md:max-w-5xl md:min-h-[430px] md:px-12 md:py-16">
                    <div
                        className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
                        style={{
                            width:
                                "80px",

                            height:
                                "80px",

                            minWidth:
                                "80px",

                            maxWidth:
                                "80px",

                            minHeight:
                                "80px",

                            maxHeight:
                                "80px",

                            boxSizing:
                                "border-box",
                        }}
                    >
                        <ScrollText
                            style={{
                                width:
                                    "40px",

                                height:
                                    "40px",
                            }}
                            strokeWidth={
                                2.5
                            }
                        />
                    </div>

                    <h1 className="text-[25px] font-black lowercase leading-none md:text-[48px]">
                        {text.pageTitle}
                    </h1>

                    <p className="mt-8 max-w-[310px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-xl md:text-[21px] md:leading-[1.35]">
                        {text.introduction}
                    </p>

                    <div className="mt-6 text-[15px] leading-[1.3] md:text-[18px]">
                        <p className="font-black">
                            {text.abbreviation}
                        </p>

                        <p className="mt-2 text-neutral-700">
                            {text.effectiveDate}
                        </p>
                    </div>

                    {text.translationNotice && (
                        <p className="mt-7 max-w-[330px] border-l-[3px] border-black pl-4 text-left text-[13px] leading-[1.4] text-neutral-600 md:max-w-2xl md:text-[16px]">
                            {text.translationNotice}
                        </p>
                    )}
                </div>

                {/* ÁSZF TARTALOM */}
                <div className="mx-auto w-full max-w-[430px] px-5 pb-20 md:max-w-5xl md:px-10 md:pb-32">
                    <div className="border-[3px] border-black bg-[#f2f2f2] px-5 py-8 md:px-12 md:py-14">
                        {text.sections.map(
                            (
                                section,
                                index
                            ) => (
                                <LegalSection
                                    key={
                                        section.title
                                    }
                                    number={
                                        index +
                                        1
                                    }
                                    title={
                                        section.title
                                    }
                                    entries={
                                        section.entries
                                    }
                                />
                            )
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}